import { api, ApiError } from "../api/client";
import type { ChatJob } from "../api/types";
import { store, type ChatMessage } from "../state";
import { escapeHtml } from "./html";

const POLL_INTERVAL_MS = 1200;
/** Com modelo local a extracao pode passar de um minuto; o teto existe para nao esperar sempre. */
const LEARNING_TIMEOUT_MS = 10 * 60 * 1000;
const PENDING_JOBS_KEY = "arti-pending-chat-jobs";

interface PendingJob {
  jobId: string;
  userId: string;
  messageId: string;
  text: string;
}

/**
 * Painel de conversa. O envio e assincrono e retomavel: o job fica registrado no armazenamento
 * local e a pagina volta a acompanha-lo depois de um refresh — com modelo local um turno pode
 * passar de um minuto, e perder a resposta por ter recarregado a aba seria perder o turno inteiro.
 */
export class ChatPanel {
  private readonly listElement: HTMLElement;
  private readonly formElement: HTMLFormElement;
  private readonly inputElement: HTMLTextAreaElement;
  private readonly submitElement: HTMLButtonElement;
  private polling = new Set<string>();

  constructor(
    private readonly root: HTMLElement,
    private readonly onTurnCompleted: () => void,
    private readonly onSuggestionsChanged: () => void
  ) {
    this.root.innerHTML = `
      <header class="panel-head">
        <div>
          <h2>Conversa</h2>
          <p class="muted" id="chat-subtitle">Pergunte, ensine, corrija.</p>
        </div>
        <button class="ghost-button" id="chat-reset" title="Iniciar uma nova conversa">Nova</button>
      </header>
      <div class="chat-list" id="chat-list" aria-live="polite"></div>
      <div class="suggestions-tray" id="suggestions-tray"></div>
      <form class="chat-form" id="chat-form">
        <textarea id="chat-input" rows="3" placeholder="Fale com o ARTI…" aria-label="Mensagem"></textarea>
        <div class="chat-form-actions">
          <span class="hint"><kbd>Ctrl</kbd>+<kbd>Enter</kbd></span>
          <button type="submit" class="primary-button" id="chat-send">Enviar</button>
        </div>
      </form>
    `;

    this.listElement = this.root.querySelector<HTMLElement>("#chat-list")!;
    this.formElement = this.root.querySelector<HTMLFormElement>("#chat-form")!;
    this.inputElement = this.root.querySelector<HTMLTextAreaElement>("#chat-input")!;
    this.submitElement = this.root.querySelector<HTMLButtonElement>("#chat-send")!;

    this.formElement.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.send();
    });

    this.inputElement.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        void this.send();
      }
    });

    this.root.querySelector("#chat-reset")!.addEventListener("click", () => {
      store.update({ conversationId: null, messages: [], lastResponse: null });
      this.render();
    });

    store.subscribe(() => this.render());
    this.render();
    this.resumePendingJobs();
  }

  private async send(): Promise<void> {
    const text = this.inputElement.value.trim();
    if (text.length === 0) {
      return;
    }

    const { userId, conversationId, responseMode } = store.state;
    const jobId = crypto.randomUUID();
    const messageId = crypto.randomUUID();

    store.upsertMessage({ id: messageId, role: "user", text });
    const placeholderId = `${messageId}-reply`;
    store.upsertMessage({ id: placeholderId, role: "assistant", text: "", pending: true });

    this.inputElement.value = "";
    this.setBusy(true);

    try {
      await api.enqueueMessage({ userId, conversationId, message: text, responseMode, jobId });
      this.rememberPendingJob({ jobId, userId, messageId: placeholderId, text });
      void this.pollJob({ jobId, userId, messageId: placeholderId, text });
    } catch (error) {
      this.setBusy(false);
      store.upsertMessage({
        id: placeholderId,
        role: "assistant",
        text: "",
        error: error instanceof ApiError ? error.message : "Não foi possível enviar a mensagem."
      });
    }
  }

  private async pollJob(job: PendingJob): Promise<void> {
    if (this.polling.has(job.jobId)) {
      return;
    }
    this.polling.add(job.jobId);

    try {
      for (;;) {
        let snapshot: ChatJob;
        try {
          snapshot = await api.getChatJob(job.jobId, job.userId);
        } catch (error) {
          if (error instanceof ApiError && error.status === 404) {
            this.failJob(job, "O processamento desta mensagem não foi encontrado.");
            return;
          }
          // Falha de rede e transitoria: o job continua rodando no servidor.
          await delay(POLL_INTERVAL_MS * 2);
          continue;
        }

        if (snapshot.status === "Completed" && snapshot.response) {
          store.upsertMessage({
            id: job.messageId,
            role: "assistant",
            text: snapshot.response.response,
            response: snapshot.response
          });
          store.update({
            conversationId: snapshot.conversationId ?? store.state.conversationId,
            lastResponse: snapshot.response
          });
          this.forgetPendingJob(job.jobId);
          this.onTurnCompleted();
          // O aprendizado corre em segundo plano e termina depois da resposta. Sem acompanhar,
          // a etiqueta "aprendendo…" ficava para sempre — anunciava um processo e nunca dizia
          // como ele acabou.
          if (snapshot.response.learningStatus === "Pending" && snapshot.response.learningJobId) {
            void this.pollLearning(job.messageId, snapshot.response.learningJobId, job.userId);
          }
          return;
        }

        if (snapshot.status === "Failed") {
          this.failJob(job, snapshot.error ?? "O processamento falhou.");
          return;
        }

        await delay(POLL_INTERVAL_MS);
      }
    } finally {
      this.polling.delete(job.jobId);
      this.setBusy(false);
    }
  }

  /**
   * Acompanha o job de extracao ate um desfecho e o registra na mensagem que o originou.
   *
   * `NothingToLearn` e desfecho legitimo, nao falha: o sistema rodou e decidiu que nao havia
   * nada persistente. Mostrar isso como erro treinaria o usuario a ignorar erros de verdade.
   */
  private async pollLearning(messageId: string, learningJobId: string, userId: string): Promise<void> {
    const deadline = Date.now() + LEARNING_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await delay(POLL_INTERVAL_MS);
      let job;
      try {
        job = await api.getLearningJob(learningJobId, userId);
      } catch {
        // Transitorio: o job segue no servidor. A proxima volta tenta de novo.
        continue;
      }

      if (job.status === "Pending" || job.status === "Running") {
        continue;
      }

      const message = store.state.messages.find((candidate) => candidate.id === messageId);
      if (message) {
        store.upsertMessage({
          ...message,
          learning: { status: job.status, suggestionCount: job.suggestionCount, error: job.error }
        });
      }

      if (job.suggestionCount > 0) {
        this.onSuggestionsChanged();
      }
      return;
    }

    // Tempo esgotado sem desfecho. Continuar mostrando "aprendendo…" seria mentir por omissao.
    const message = store.state.messages.find((candidate) => candidate.id === messageId);
    if (message) {
      store.upsertMessage({
        ...message,
        learning: { status: "Failed", suggestionCount: 0, error: "O aprendizado excedeu o tempo de espera." }
      });
    }
  }

  private failJob(job: PendingJob, message: string): void {
    store.upsertMessage({ id: job.messageId, role: "assistant", text: "", error: message });
    this.forgetPendingJob(job.jobId);
  }

  private setBusy(busy: boolean): void {
    this.submitElement.disabled = busy;
    this.submitElement.textContent = busy ? "Pensando…" : "Enviar";
  }

  private rememberPendingJob(job: PendingJob): void {
    const jobs = this.readPendingJobs().filter((candidate) => candidate.jobId !== job.jobId);
    jobs.push(job);
    this.writePendingJobs(jobs);
  }

  private forgetPendingJob(jobId: string): void {
    this.writePendingJobs(this.readPendingJobs().filter((candidate) => candidate.jobId !== jobId));
  }

  private resumePendingJobs(): void {
    for (const job of this.readPendingJobs()) {
      if (job.userId !== store.state.userId) {
        continue;
      }
      store.upsertMessage({ id: `${job.messageId}-echo`, role: "user", text: job.text });
      store.upsertMessage({ id: job.messageId, role: "assistant", text: "", pending: true });
      this.setBusy(true);
      void this.pollJob(job);
    }
  }

  private readPendingJobs(): PendingJob[] {
    try {
      return JSON.parse(localStorage.getItem(PENDING_JOBS_KEY) ?? "[]") as PendingJob[];
    } catch {
      return [];
    }
  }

  private writePendingJobs(jobs: PendingJob[]): void {
    try {
      localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(jobs));
    } catch {
      // Sem armazenamento a retomada nao acontece; o turno atual continua funcionando.
    }
  }

  private render(): void {
    const { messages } = store.state;
    if (messages.length === 0) {
      this.listElement.innerHTML = `
        <div class="chat-empty">
          <p>Ensine algo ou faça uma pergunta.</p>
          <p class="muted">O que você ensinar vira uma sugestão para aprovar aqui embaixo. Só depois de aprovada ela vira um nó no grafo.</p>
        </div>
      `;
      return;
    }

    this.listElement.innerHTML = messages.map((message) => this.renderMessage(message)).join("");
    this.listElement.scrollTop = this.listElement.scrollHeight;
  }

  private renderMessage(message: ChatMessage): string {
    if (message.error) {
      return `<article class="bubble assistant error"><p>${escapeHtml(message.error)}</p></article>`;
    }

    if (message.pending) {
      return `<article class="bubble assistant pending"><span class="dots"><i></i><i></i><i></i></span></article>`;
    }

    const footer = message.response ? this.renderFooter(message.response, message) : "";
    return `
      <article class="bubble ${message.role}">
        <p>${escapeHtml(message.text)}</p>
        ${footer}
      </article>
    `;
  }

  /**
   * O rodape distingue o que a ARTI SABE do que ela CONSULTOU neste turno. Sao coisas diferentes
   * e a tela nao pode borra-las: um grafo cheio nao significa que ele decidiu a resposta.
   */
  private renderFooter(response: import("../api/types").ChatResponse, message: ChatMessage): string {
    const tags: string[] = [];
    if (response.memoryUsed) {
      tags.push(`<span class="tag used">memória (${response.memorySourceIds?.length ?? 0})</span>`);
    }
    if (response.knowledgeUsed) {
      tags.push(`<span class="tag used">conhecimento (${response.knowledgeSourceIds?.length ?? 0})</span>`);
    }
    if (response.cognitiveGraphUsed) {
      tags.push(`<span class="tag graph">grafo</span>`);
    }

    const learningTag = this.renderLearningTag(response, message);
    if (learningTag) {
      tags.push(learningTag);
    }

    tags.push(`<span class="tag quiet">${(response.elapsedMs / 1000).toFixed(1)}s</span>`);
    return `<footer class="bubble-meta">${tags.join("")}</footer>`;
  }

  /**
   * Estado do aprendizado desta mensagem, do inicio ao desfecho. Cada caso e distinto de
   * proposito: "nada a aprender" e decisao do sistema, "falhou" e defeito, e o silencio de
   * antes — a etiqueta eterna — nao dizia nem uma coisa nem outra.
   */
  private renderLearningTag(
    response: import("../api/types").ChatResponse,
    message: ChatMessage
  ): string | null {
    const outcome = message.learning;
    if (!outcome) {
      return response.learningStatus === "Pending"
        ? `<span class="tag learning">aprendendo…</span>`
        : null;
    }

    if (outcome.status === "Failed") {
      const detail = outcome.error ? ` title="${escapeHtml(outcome.error)}"` : "";
      return `<span class="tag failed"${detail}>aprendizado falhou</span>`;
    }

    if (outcome.status === "NothingToLearn" || outcome.suggestionCount === 0) {
      return `<span class="tag quiet">nada a aprender</span>`;
    }

    const plural = outcome.suggestionCount === 1 ? "sugestão" : "sugestões";
    return `<span class="tag learning">${outcome.suggestionCount} ${plural} para aprovar</span>`;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
